/**
 * SimulationContext
 * React Context for simulation state management
 * Requirements: 3.2, 3.4, 4.3, 4.4
 */

import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { RouteType, RBRouteType, CoverageType, FormationType, PlayerPositions, SimulationState, Position } from '../types';

// Default initial positions for players
const DEFAULT_WR_POSITION: Position = { x: 20, y: 85 };
const DEFAULT_WR2_POSITION: Position = { x: 30, y: 90 };
const DEFAULT_WR3_POSITION: Position = { x: 70, y: 90 };
const DEFAULT_WR4_POSITION: Position = { x: 80, y: 85 };
const DEFAULT_RB_POSITION: Position = { x: 55, y: 92 };
const DEFAULT_CB1_POSITION: Position = { x: 20, y: 65 };
const DEFAULT_CB2_POSITION: Position = { x: 80, y: 65 };
const DEFAULT_DBLB1_POSITION: Position = { x: 30, y: 75 };
const DEFAULT_DBLB2_POSITION: Position = { x: 70, y: 75 };
const DEFAULT_FS_POSITION: Position = { x: 35, y: 40 };
const DEFAULT_SS_POSITION: Position = { x: 65, y: 40 };
const DEFAULT_LB_POSITION: Position = { x: 50, y: 70 };

// Default initial state
const DEFAULT_STATE: SimulationState = {
  selectedFormation: 'doubles',
  selectedRoute: 'slant',
  selectedRoute2: 'go',
  selectedRoute3: 'slant',
  selectedRoute4: 'go',
  selectedRBRoute: 'out',
  selectedCoverage: 'man',
  selectedCustomDefenseId: null,
  isPlaying: false,
  playbackSpeed: 1,
  playerPositions: {
    wr: DEFAULT_WR_POSITION,
    wr2: DEFAULT_WR2_POSITION,
    wr3: DEFAULT_WR3_POSITION,
    wr4: DEFAULT_WR4_POSITION,
    rb: DEFAULT_RB_POSITION,
    cb1: DEFAULT_CB1_POSITION,
    cb2: DEFAULT_CB2_POSITION,
    dblb1: DEFAULT_DBLB1_POSITION,
    dblb2: DEFAULT_DBLB2_POSITION,
    fs: DEFAULT_FS_POSITION,
    ss: DEFAULT_SS_POSITION,
    lb: DEFAULT_LB_POSITION,
  },
};

// Context value interface including state and update functions
interface SimulationContextValue {
  // State
  state: SimulationState;
  
  // State update functions
  setSelectedFormation: (formation: FormationType) => void;
  setSelectedRoute: (route: RouteType) => void;
  setSelectedRoute2: (route: RouteType) => void;
  setSelectedRoute3: (route: RouteType) => void;
  setSelectedRoute4: (route: RouteType) => void;
  setSelectedRBRoute: (route: RBRouteType) => void;
  setSelectedCoverage: (coverage: CoverageType) => void;
  setSelectedCustomDefenseId: (id: string | null) => void;
  setIsPlaying: (isPlaying: boolean) => void;
  setPlaybackSpeed: (speed: number) => void;
  setPlayerPositions: (positions: PlayerPositions) => void;
  updateWRPosition: (position: Position) => void;
  updateWR2Position: (position: Position) => void;
  updateWR3Position: (position: Position) => void;
  updateWR4Position: (position: Position) => void;
  updateRBPosition: (position: Position) => void;
  updateCB1Position: (position: Position) => void;
  updateCB2Position: (position: Position) => void;
  updateDBLB1Position: (position: Position) => void;
  updateDBLB2Position: (position: Position) => void;
  updateFSPosition: (position: Position) => void;
  updateSSPosition: (position: Position) => void;
  resetPositions: () => void;
  resetState: () => void;
}

// Create context with undefined default (will be provided by provider)
const SimulationContext = createContext<SimulationContextValue | undefined>(undefined);

// Provider props interface
interface SimulationProviderProps {
  children: ReactNode;
  initialState?: Partial<SimulationState>;
}

/**
 * SimulationProvider component
 * Provides simulation state and update functions to child components
 */
export function SimulationProvider({ children, initialState }: SimulationProviderProps) {
  // Merge initial state with defaults
  const mergedInitialState: SimulationState = useMemo(() => ({
    ...DEFAULT_STATE,
    ...initialState,
    playerPositions: {
      ...DEFAULT_STATE.playerPositions,
      ...initialState?.playerPositions,
    },
  }), [initialState]);

  const [state, setState] = useState<SimulationState>(mergedInitialState);

  // Route selection - Requirements 3.2, 3.4
  const setSelectedRoute = useCallback((route: RouteType) => {
    setState((prev) => ({ ...prev, selectedRoute: route }));
  }, []);

  // Formation selection
  const setSelectedFormation = useCallback((formation: FormationType) => {
    setState((prev) => ({ ...prev, selectedFormation: formation }));
  }, []);

  // Route 2 selection for WR2
  const setSelectedRoute2 = useCallback((route: RouteType) => {
    setState((prev) => ({ ...prev, selectedRoute2: route }));
  }, []);

  // Route 3 selection for WR3
  const setSelectedRoute3 = useCallback((route: RouteType) => {
    setState((prev) => ({ ...prev, selectedRoute3: route }));
  }, []);

  // Route 4 selection for WR4
  const setSelectedRoute4 = useCallback((route: RouteType) => {
    setState((prev) => ({ ...prev, selectedRoute4: route }));
  }, []);

  // RB route selection
  const setSelectedRBRoute = useCallback((route: RBRouteType) => {
    setState((prev) => ({ ...prev, selectedRBRoute: route }));
  }, []);

  // Coverage selection - Requirements 4.3, 4.4
  const setSelectedCoverage = useCallback((coverage: CoverageType) => {
    setState((prev) => ({ ...prev, selectedCoverage: coverage }));
  }, []);

  // Custom defense selection - Requirements 3.1, 3.2, 7.1, 7.2
  const setSelectedCustomDefenseId = useCallback((id: string | null) => {
    setState((prev) => ({ ...prev, selectedCustomDefenseId: id }));
  }, []);

  // Play state management
  const setIsPlaying = useCallback((isPlaying: boolean) => {
    setState((prev) => ({ ...prev, isPlaying }));
  }, []);

  // Playback speed management
  const setPlaybackSpeed = useCallback((speed: number) => {
    setState((prev) => ({ ...prev, playbackSpeed: speed }));
  }, []);

  // Player position management
  const setPlayerPositions = useCallback((positions: PlayerPositions) => {
    setState((prev) => ({ ...prev, playerPositions: positions }));
  }, []);

  const updateWRPosition = useCallback((position: Position) => {
    setState((prev) => ({
      ...prev,
      playerPositions: { ...prev.playerPositions, wr: position },
    }));
  }, []);

  const updateWR2Position = useCallback((position: Position) => {
    setState((prev) => ({
      ...prev,
      playerPositions: { ...prev.playerPositions, wr2: position },
    }));
  }, []);

  const updateWR3Position = useCallback((position: Position) => {
    setState((prev) => ({
      ...prev,
      playerPositions: { ...prev.playerPositions, wr3: position },
    }));
  }, []);

  const updateWR4Position = useCallback((position: Position) => {
    setState((prev) => ({
      ...prev,
      playerPositions: { ...prev.playerPositions, wr4: position },
    }));
  }, []);

  const updateRBPosition = useCallback((position: Position) => {
    setState((prev) => ({
      ...prev,
      playerPositions: { ...prev.playerPositions, rb: position },
    }));
  }, []);

  const updateCB1Position = useCallback((position: Position) => {
    setState((prev) => ({
      ...prev,
      playerPositions: { ...prev.playerPositions, cb1: position },
    }));
  }, []);

  const updateCB2Position = useCallback((position: Position) => {
    setState((prev) => ({
      ...prev,
      playerPositions: { ...prev.playerPositions, cb2: position },
    }));
  }, []);

  const updateDBLB1Position = useCallback((position: Position) => {
    setState((prev) => ({
      ...prev,
      playerPositions: { ...prev.playerPositions, dblb1: position },
    }));
  }, []);

  const updateDBLB2Position = useCallback((position: Position) => {
    setState((prev) => ({
      ...prev,
      playerPositions: { ...prev.playerPositions, dblb2: position },
    }));
  }, []);

  const updateFSPosition = useCallback((position: Position) => {
    setState((prev) => ({
      ...prev,
      playerPositions: { ...prev.playerPositions, fs: position },
    }));
  }, []);

  const updateSSPosition = useCallback((position: Position) => {
    setState((prev) => ({
      ...prev,
      playerPositions: { ...prev.playerPositions, ss: position },
    }));
  }, []);

  // Reset functions
  const resetPositions = useCallback(() => {
    setState((prev) => ({
      ...prev,
      playerPositions: {
        wr: DEFAULT_WR_POSITION,
        wr2: DEFAULT_WR2_POSITION,
        wr3: DEFAULT_WR3_POSITION,
        wr4: DEFAULT_WR4_POSITION,
        rb: DEFAULT_RB_POSITION,
        cb1: DEFAULT_CB1_POSITION,
        cb2: DEFAULT_CB2_POSITION,
        dblb1: DEFAULT_DBLB1_POSITION,
        dblb2: DEFAULT_DBLB2_POSITION,
        fs: DEFAULT_FS_POSITION,
        ss: DEFAULT_SS_POSITION,
        lb: DEFAULT_LB_POSITION,
      },
    }));
  }, []);

  const resetState = useCallback(() => {
    setState(mergedInitialState);
  }, [mergedInitialState]);

  const value: SimulationContextValue = {
    state,
    setSelectedFormation,
    setSelectedRoute,
    setSelectedRoute2,
    setSelectedRoute3,
    setSelectedRoute4,
    setSelectedRBRoute,
    setSelectedCoverage,
    setSelectedCustomDefenseId,
    setIsPlaying,
    setPlaybackSpeed,
    setPlayerPositions,
    updateWRPosition,
    updateWR2Position,
    updateWR3Position,
    updateWR4Position,
    updateRBPosition,
    updateCB1Position,
    updateCB2Position,
    updateDBLB1Position,
    updateDBLB2Position,
    updateFSPosition,
    updateSSPosition,
    resetPositions,
    resetState,
  };

  return (
    <SimulationContext.Provider value={value}>
      {children}
    </SimulationContext.Provider>
  );
}

/**
 * Custom hook to access simulation context
 * Throws error if used outside of SimulationProvider
 */
export function useSimulation(): SimulationContextValue {
  const context = useContext(SimulationContext);
  if (context === undefined) {
    throw new Error('useSimulation must be used within a SimulationProvider');
  }
  return context;
}

// Export default positions for use in other components
export { DEFAULT_WR_POSITION, DEFAULT_WR2_POSITION, DEFAULT_WR3_POSITION, DEFAULT_WR4_POSITION, DEFAULT_RB_POSITION, DEFAULT_CB1_POSITION, DEFAULT_CB2_POSITION, DEFAULT_DBLB1_POSITION, DEFAULT_DBLB2_POSITION, DEFAULT_FS_POSITION, DEFAULT_SS_POSITION, DEFAULT_LB_POSITION, DEFAULT_STATE };
